import {
    useState, useEffect, useContext, useRef,
} from 'react';
import styled from 'styled-components';

// Material UI
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import GetAppIcon from '@mui/icons-material/GetApp';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// Local
import { createBlobURL, freeBlobURL } from '../../../ui_utils';
import { App } from '../../../../BMapsApp';
import { useColorSchemeInfo } from '../../../../redux/prefs/access';
import { ExportPaneContext } from '../ExportPane';
import {
    setBackgroundColor, getImageBlob, supportsRayTracedImage, rayTracedImageSize,
} from '../../../../MainCanvas';

export const IMAGE_TYPE = {
    '3D': '3D',
    '2D': '2D',
};

/** How the 3D image is made: the screen frame, or the visualizer's ray tracer (if it has one). */
export const RENDER_MODE = {
    screen: 'screen',
    ray: 'ray',
};

/**
 * Ray-traced export size. A figure is specified the way a journal asks for one - so wide on the
 * page, at so many dots per inch - and those two give the pixel size; `px` is for a size in
 * pixels, where the resolution is then only what the image is tagged with.
 */
export const RAY_UNITS = ['in', 'cm', 'px'];
/** A two-column figure at the resolution journals ask for: 1950 px across. */
export const RAY_DEFAULTS = { width: '6.5', unit: 'in', dpi: '300' };
const CmPerInch = 2.54;

/** @returns {number} pixels across, or 0 when the width (or the DPI it needs) is not a number */
export function widthInPixels(width, unit, dpi) {
    const w = Number(width);
    if (!(w > 0)) return 0;
    if (unit === 'px') return Math.round(w);
    const d = Number(dpi);
    if (!(d > 0)) return 0;
    return Math.round((unit === 'cm' ? w / CmPerInch : w) * d);
}

/**
 * The line under the size fields: what Render will actually produce, in pixels and on paper.
 * @param {{wantedPixels: number, raySize: ?{width: number, height: number}, capped: boolean,
 *     dpi: string|number, unit: string}} state
 */
export function rayImageSizeText({
    wantedPixels, raySize, capped, dpi, unit,
}) {
    if (!(wantedPixels > 0)) {
        return unit === 'px' ? 'Enter a width in pixels.' : 'Enter a width and a resolution.';
    }
    // before the 3D view exists there is no aspect ratio to give the height
    if (!raySize) return `${wantedPixels} px wide`;
    const pixels = `${raySize.width} × ${raySize.height} px`;
    if (capped) {
        return `${pixels} — as large as the ray tracer renders (${wantedPixels} px asked for)`;
    }
    const d = Number(dpi);
    if (!(d > 0)) return pixels;
    const scale = unit === 'cm' ? CmPerInch / d : 1 / d;
    const on = `${(raySize.width * scale).toFixed(1)} × ${(raySize.height * scale).toFixed(1)}`;
    return `${pixels} · ${on} ${unit === 'cm' ? 'cm' : 'in'} at ${d} DPI`;
}

/** Grey-and-white checkerboard behind a preview with a transparent background. */
const TransparencyBackdrop = {
    backgroundImage: 'repeating-conic-gradient(#cccccc 0% 25%, #ffffff 0% 50%)',
    backgroundSize: '16px 16px',
};

/** Resolve once the browser has painted - so a busy state is visible before a blocking task. */
function nextPaint() {
    return new Promise((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        } else {
            setTimeout(resolve, 0);
        }
    });
}

/** @returns {Promise<{width: number, height: number}|null>} */
async function imagePixelSize(blob) {
    if (typeof createImageBitmap !== 'function') return null;
    try {
        const bitmap = await createImageBitmap(blob);
        const size = { width: bitmap.width, height: bitmap.height };
        if (typeof bitmap.close === 'function') bitmap.close();
        return size;
    } catch (ex) {
        return null;
    }
}

const ToggleButton = styled(Button).attrs((props) => ({
    variant: props.selected ? 'contained' : 'outlined',
}))`
    border: solid ${({ theme }) => theme.palette.primary.main } 1px;
    background-color: ${({ selected, theme }) => (selected ? theme.palette.primary.main : theme.palette.primary.contrastText)};
    color: ${({ selected, theme }) => (selected ? theme.palette.primary.contrastText : theme.palette.primary.main) };

    &:hover {
        background-color: ${({ selected, theme }) => (selected ? theme.palette.primary.main : theme.palette.primary.contrastText)};
    }
`;

export default function DownloadImageTab({ selected }) {
    const { isMobile } = useContext(ExportPaneContext);
    const [imageType, setImageType] = useState(IMAGE_TYPE['3D']);
    const [link3d, setLink3d] = useState(null);
    const [link2d, setLink2d] = useState(null);
    const colorSchemeInfo = useColorSchemeInfo();

    // Ray-traced 3D image (only offered when the visualizer has a ray tracer)
    const rayAvailable = supportsRayTracedImage();
    const [renderMode, setRenderMode] = useState(RENDER_MODE.screen);
    const [rayWidth, setRayWidth] = useState(RAY_DEFAULTS.width);
    const [rayUnit, setRayUnit] = useState(RAY_DEFAULTS.unit);
    const [rayDPI, setRayDPI] = useState(RAY_DEFAULTS.dpi);
    const [rayOutline, setRayOutline] = useState(false);
    const [rayTransparent, setRayTransparent] = useState(false);
    const [rayLink, setRayLink] = useState(null);
    /** null | {busy: true} | {info: string} | {error: string} */
    const [rayStatus, setRayStatus] = useState(null);
    /** The live object URL per image slot ('3d' | '2d' | 'ray'): a ray-traced PNG is MBs. */
    const blobURLs = useRef({});
    function trackBlobURL(slot, url) {
        const previous = blobURLs.current[slot];
        if (previous && previous !== url) freeBlobURL(previous);
        blobURLs.current[slot] = url || null;
    }
    useEffect(() => () => {
        Object.values(blobURLs.current).forEach((url) => url && freeBlobURL(url));
        blobURLs.current = {};
    }, []);
    const rayMode = rayAvailable && renderMode === RENDER_MODE.ray;
    /** What Render would produce: the asked-for width, and the size the backend would allow. */
    const wantedPixels = widthInPixels(rayWidth, rayUnit, rayDPI);
    const raySize = rayMode && wantedPixels > 0
        ? rayTracedImageSize({ width: wantedPixels }) : null;
    const rayCapped = !!(raySize && raySize.width < wantedPixels);

    /** A changed option makes the last ray-traced image stale. */
    function changeRayOption(setter) {
        return (value) => {
            setter(value);
            setRayLink(null);
            trackBlobURL('ray', null);
            setRayStatus(null);
        };
    }

    function imageLinkFor(blob, downloadName, { transparent=false, slot='3d' }={}) {
        const data = createBlobURL(blob);
        trackBlobURL(slot, data);
        const imgTag = (
            <img
                alt="3D Structure"
                data-transparent={transparent ? 'true' : undefined}
                style={{
                    maxWidth: '100%',
                    height: isMobile ? '100%' : 'unset',
                    ...(transparent ? TransparencyBackdrop : {}),
                }}
                src={`${data}`}
            />
        );
        return (
            <>
                <a id="export-pane_download-image-link" href={`${data}`} download={`${downloadName}`} target="_blank" rel="noreferrer">
                    {imgTag}
                </a>
                {transparent && (
                    <Typography variant="caption" display="block" align="center" color="textSecondary">
                        Checkerboard = transparent (not part of the image)
                    </Typography>
                )}
            </>
        );
    }

    async function renderRayTraced() {
        setRayStatus({ busy: true });
        setRayLink(null);
        // The ray tracer runs on the main thread and blocks the page: show "Rendering..." first.
        await nextPaint();
        const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
        try {
            const blob = await withBackgroundColorTweak(() => getImageBlob({
                ray: true,
                width: wantedPixels,
                dpi: Number(rayDPI) > 0 ? Number(rayDPI) : 0,
                outline: rayOutline,
                transparentBackground: rayTransparent,
            }), colorSchemeInfo);
            if (!blob) throw new Error('The ray tracer returned no image.');
            const ended = typeof performance !== 'undefined' ? performance.now() : Date.now();
            const size = await imagePixelSize(blob);
            setRayLink(imageLinkFor(blob, 'BMaps-Canvas-raytraced.png', {
                transparent: rayTransparent, slot: 'ray',
            }));
            const sizeText = size ? `${size.width} × ${size.height} px, ` : '';
            setRayStatus({ info: `${sizeText}rendered in ${((ended - started) / 1000).toFixed(1)} s` });
        } catch (ex) {
            console.error('Ray-traced image failed', ex);
            setRayStatus({ error: (ex && ex.message) || String(ex) });
        }
    }

    function handleDownload() {
        const link = document.querySelector('#export-pane_download-image-link');
        link.click();
    }

    async function canvasToBlobURL(canvas) {
        const blob = await new Promise((resolve) => {
            // defaults to an image/png type blob
            canvas.toBlob((blobData) => resolve(blobData));
        });
        return createBlobURL(blob);
    }

    function svgToBlobURL(svg) {
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        return createBlobURL(blob);
    }

    async function getDownload3dImageLink() {
        const downloadName = 'BMaps-Canvas.png';
        // The visualizer owns its canvas; ask it for the image rather than querying the DOM.
        const blob = await withBackgroundColorTweak(() => getImageBlob(), colorSchemeInfo);
        if (!blob) {
            throw new Error("Couldn't capture the 3D canvas image from the visualizer");
        }
        return imageLinkFor(blob, downloadName);
    }

    async function getDownload2dImageLink() {
        let downloadName = '';
        let imgData = '';
        const compound = App.Workspace.getActiveCompound();

        if (!compound) {
            return null;
        }

        const svgData = compound.getSvg();
        if (!svgData || svgData === 'failed') {
            downloadName = 'BMaps-2dCompound.png';
            // Unfortunate inconsistency here:
            // SmilesDrawer has a canvas with an id of activeCompoundId
            // 3DCanvasViewer has a canvas that is a child of #activeCompoundId
            const canvas = document.querySelector('canvas#activeCompound2d') || document.querySelector('#activeCompound2d canvas');
            imgData = canvas ? (await canvasToBlobURL(canvas)) : '';
        } else {
            downloadName = 'BMaps-2dCompound.svg';
            imgData = svgToBlobURL(svgData);
        }
        trackBlobURL('2d', imgData || null);

        if (!imgData) {
            return null;
        }

        const imgTag = (
            <img
                alt="2D Structure"
                style={{
                    backgroundColor: `${ colorSchemeInfo.css }`,
                    maxWidth: '100%',
                    maxHeight: '98%',
                }}
                src={imgData}
            />
        );
        const link = (
            <a id="export-pane_download-image-link" href={`${imgData}`} download={`${downloadName}`} target="_blank" rel="noreferrer">
                {imgTag}
            </a>
        );
        return link;
    }

    useEffect(() => {
        if (selected) {
            (async () => {
                setLink3d(await getDownload3dImageLink());
                setLink2d(await getDownload2dImageLink());
            })();
        }
    }, [selected]);

    let imageLink = null;
    let emptyMessage = 'No image to display...';

    if (imageType === IMAGE_TYPE['3D'] && rayMode) {
        imageLink = rayLink;
        if (!rayLink) emptyMessage = 'Press Render to ray-trace the current 3D view.';
    } else if (imageType === IMAGE_TYPE['3D'] && link3d !== null) {
        imageLink = link3d;
    } else if (link2d !== null) {
        imageLink = link2d;
    }

    const rayBusy = !!(rayStatus && rayStatus.busy);
    const showScreenImage = () => setRenderMode(RENDER_MODE.screen);
    const showRayTraced = () => setRenderMode(RENDER_MODE.ray);
    const onRayWidthChange = (e) => changeRayOption(setRayWidth)(e.target.value);
    const onRayUnitChange = (e) => changeRayOption(setRayUnit)(e.target.value);
    const onRayDPIChange = (e) => changeRayOption(setRayDPI)(e.target.value);
    const onRayOutlineChange = (e) => changeRayOption(setRayOutline)(e.target.checked);
    const onRayTransparentChange = (e) => changeRayOption(setRayTransparent)(e.target.checked);

    return (
        <Grid container sx={{ flexDirection: 'column', alignItems: 'center' }} spacing={3}>
            <Grid container wrap="nowrap" sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <ButtonGroup fullWidth={isMobile} style={{ marginRight: '1em' }}>
                    <ToggleButton selected={imageType === IMAGE_TYPE['3D']} onClick={() => setImageType(IMAGE_TYPE['3D'])}>
                        {isMobile ? '3D' : '3D Workspace Image' }
                    </ToggleButton>
                    <ToggleButton selected={imageType === IMAGE_TYPE['2D']} onClick={() => setImageType(IMAGE_TYPE['2D'])}>
                        {isMobile ? '2D' : '2D Workspace Image' }
                    </ToggleButton>
                </ButtonGroup>
                <IconButton disabled={!imageLink || rayBusy} onClick={handleDownload} size="large">
                    <GetAppIcon />
                </IconButton>
            </Grid>
            {imageType === IMAGE_TYPE['3D'] && rayAvailable && (
                <Grid container sx={{ flexDirection: 'column', alignItems: 'center', gap: '0.5em' }} id="export-pane_ray-options">
                    <ButtonGroup size="small">
                        <ToggleButton selected={!rayMode} onClick={showScreenImage}>
                            Screen image
                        </ToggleButton>
                        <ToggleButton selected={rayMode} onClick={showRayTraced}>
                            Ray-traced
                        </ToggleButton>
                    </ButtonGroup>
                    {rayMode && (
                        <>
                            <Typography variant="caption">Ray-traced image · Includes PyMOL™</Typography>
                            <Grid container sx={{ justifyContent: 'center', alignItems: 'center', gap: '0.5em 1em' }}>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Width"
                                    value={rayWidth}
                                    onChange={onRayWidthChange}
                                    disabled={rayBusy}
                                    sx={{ width: '6em' }}
                                />
                                <TextField
                                    select
                                    size="small"
                                    label="Units"
                                    value={rayUnit}
                                    onChange={onRayUnitChange}
                                    disabled={rayBusy}
                                    sx={{ width: '5em' }}
                                >
                                    {RAY_UNITS.map((unit) => (
                                        <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Resolution (DPI)"
                                    value={rayDPI}
                                    onChange={onRayDPIChange}
                                    disabled={rayBusy}
                                    sx={{ width: '9em' }}
                                />
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={rayOutline}
                                            onChange={onRayOutlineChange}
                                            disabled={rayBusy}
                                        />
                                    )}
                                    label="Outline"
                                />
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={rayTransparent}
                                            onChange={onRayTransparentChange}
                                            disabled={rayBusy}
                                        />
                                    )}
                                    label="Transparent background"
                                />
                                <Button variant="contained" onClick={renderRayTraced} disabled={rayBusy}>
                                    {rayBusy ? 'Rendering…' : 'Render'}
                                </Button>
                            </Grid>
                            <Typography
                                variant="caption"
                                id="export-pane_ray-size"
                                color={rayCapped ? 'error' : 'textSecondary'}
                            >
                                {rayImageSizeText({
                                    wantedPixels,
                                    raySize,
                                    capped: rayCapped,
                                    dpi: rayDPI,
                                    unit: rayUnit,
                                })}
                            </Typography>
                            <Typography variant="caption" color={rayStatus && rayStatus.error ? 'error' : 'textSecondary'}>
                                {(rayStatus && rayStatus.error)
                                    || (rayStatus && rayStatus.info)
                                    || (rayBusy
                                        ? 'Ray-tracing... the page pauses until the image is ready.'
                                        : 'Rendering takes a few seconds and pauses the page.')}
                            </Typography>
                        </>
                    )}
                </Grid>
            )}
            {imageLink ? (
                <Grid>
                    {imageLink}
                </Grid>
            ) : emptyMessage}
        </Grid>
    );
}

/**
 * Temporarily change the color of the 3D canvas before doing something.
 * Note: as written, this only works for the 3D image.
 * The 2D images are produced differently.
 * In normal operation, the 2D image is cached as an svg on the compound.
 * If that fails, the image is pulled from the 2D canvas, for which there isn't
 * an easy path to temporarily change the background color.
 * @param {*} doFn A function to do with the temporary background color. The return is passed along.
 * @param {object} originalColorSchemeInfo The original colorSchemeInfo which is used to determine
 * if a tweak is needed and how to restore the original color.
 */
async function withBackgroundColorTweak(doFn, originalColorSchemeInfo) {
    // map color scheme names to the "tweaked" background color hex values
    const backgroundColorTweaks = { white: 0xffffff };
    const backgroundColorTweak = backgroundColorTweaks[originalColorSchemeInfo.name];
    if (backgroundColorTweak) {
        setBackgroundColor(backgroundColorTweak);
    }
    try {
        // Await so the original background is not restored before an asynchronous
        // capture has finished.
        return await doFn();
    } finally {
        if (backgroundColorTweak) {
            setBackgroundColor(originalColorSchemeInfo);
        }
    }
}
